/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UnknownAction, Dispatch } from '@reduxjs/toolkit';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { i18n, TFunction } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import {
  createWorkspace,
  deleteWorkspace,
  getWorkspaceLabelsConfig,
} from './api';
import { workspaceQueryOptions } from './queryOptions';
import type {
  WorkspaceLabelsConfigResponse,
  WorkspacePresetForBE,
  WorkspacePresetListItem,
} from '../../store/workspace/types';
import { WorkspacePresetAction } from '../../store/workspace/types';
import {
  workspaceListActions,
  workspaceListSelectors,
} from '../../store/workspaceList';
import {
  WorkspaceListErrorCategory,
  WorkspaceListErrorType,
} from '../../store/workspaceList/types';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { getIdFromUrl } from '../utils';

export const useWorkspaceLabelsConfig =
  (): UseQueryResult<WorkspaceLabelsConfigResponse> => {
    const { auth } = useAuthenticationContext();
    return useQuery({
      queryKey: ['workspace-labels-config'],
      queryFn: async () => getWorkspaceLabelsConfig(auth!),
    });
  };

export const useWorkspacePresets = (): UseQueryResult<
  WorkspacePresetListItem[]
> => {
  const { auth } = useAuthenticationContext();
  return useQuery(workspaceQueryOptions.getWorkspacePresets(auth!));
};

type MutationReturnType<T> = UseMutationResult<Headers | null, Error, T>;

const onWorkspaceApiError = (
  dispatch: Dispatch<UnknownAction>,
  error: Error,
  t: TFunction<'workspace', i18n>,
): void => {
  dispatch(
    workspaceListActions.onErrorWorkspacePresetAction({
      error: {
        message: `${error.message} ${t('workspace-error-generic')}`,
        type: WorkspaceListErrorType.VERBATIM_ERROR,
        category:
          error.message === 'Authentication context not found'
            ? WorkspaceListErrorCategory.AUTH
            : WorkspaceListErrorCategory.GENERIC,
      },
    }),
  );
};

export const useDeleteWorkspacePreset = (): MutationReturnType<{
  id: string;
}> => {
  const { auth } = useAuthenticationContext();
  const { title } = useSelector(
    workspaceListSelectors.getWorkspaceActionFormValues,
  );
  const { t } = useWorkspaceTranslation();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ id }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return deleteWorkspace(id, auth);
    },
    onSuccess: (data, variables) => {
      dispatch(
        workspaceListActions.onSuccessWorkspacePresetAction({
          workspaceId: variables.id,
          action: WorkspacePresetAction.DELETE,
          emptyWorkspacePresetTitle: t('workspace-new'),
          title: title!,
        }),
      );
    },
    onError: (error) => {
      onWorkspaceApiError(dispatch, error, t);
    },
  });
};

export const useCreateWorkspacePreset = (): MutationReturnType<{
  data: WorkspacePresetForBE;
  action?: WorkspacePresetAction.DUPLICATE;
}> => {
  const { auth } = useAuthenticationContext();
  const { t } = useWorkspaceTranslation();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ data }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return createWorkspace(data, auth);
    },
    onSuccess: (data, variables) => {
      const location = data?.get('location');
      const newId = getIdFromUrl(location!);
      const scope = variables.data.scope || 'user';

      dispatch(
        workspaceListActions.onSuccessWorkspacePresetAction({
          workspaceId: newId,
          action: variables.action || WorkspacePresetAction.SAVE_AS,
          title: variables.data.title!,
          scope,
          username: variables.data.username || '',
        }),
      );
    },
    onError: (error) => {
      onWorkspaceApiError(dispatch, error, t);
    },
  });
};
